/**
 * TOS Template Manager - Simple admin page to edit TOS text for each option
 * Stored in: ClientDb.tos_templates
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/api';
import './TOSTemplateManager.css';

// The 8 TOS options
const OPTIONS = [
  // Monetization Only (15%, 20%)
  { key: 'monetization-15', label: '15% Monetization Only' },
  { key: 'monetization-20', label: '20% Monetization Only' },
  
  // Management Only (15%, 20%, 25%)
  { key: 'management-15', label: '15% Management Only' },
  { key: 'management-20', label: '20% Management Only' },
  { key: 'management-25', label: '25% Management Only' },
  
  // Management Add-On (5%, 10%, 15%)
  { key: 'addon-5', label: '5% Management Add-On' },
  { key: 'addon-10', label: '10% Management Add-On' },
  { key: 'addon-15', label: '15% Management Add-On' },
];

function TOSTemplateManager() {
  const { getAccessToken } = useAuth();
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);
  const [content, setContent] = useState('');
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTemplateKey, setNewTemplateKey] = useState('');
  const [newTemplateLabel, setNewTemplateLabel] = useState('');
  const [customOptions, setCustomOptions] = useState([]);

  // Load all templates on mount
  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const token = await getAccessToken();
      const response = await axios.get(
        API_ENDPOINTS.TOS_TEMPLATES?.LIST || '/api/tos-templates',
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Convert array to object keyed by key
      const templateMap = {};
      const customOpts = [];
      (response.data || []).forEach(t => {
        templateMap[t.key] = t.content;
        // If not in the default OPTIONS, add to custom
        if (!OPTIONS.find(opt => opt.key === t.key)) {
          customOpts.push({
            key: t.key,
            label: t.label || t.key.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
          });
        }
      });
      setTemplates(templateMap);
      setCustomOptions(customOpts);
    } catch (err) {
      console.error('Error loading templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (option) => {
    setSelected(option);
    setContent(templates[option.key] || '');
    setSuccess(null);
    setError(null);
  };

  const handleSave = async () => {
    if (!selected) return;
    
    try {
      setSaving(true);
      setError(null);
      
      const token = await getAccessToken();
      await axios.post(
        API_ENDPOINTS.TOS_TEMPLATES?.SAVE || '/api/tos-templates',
        { key: selected.key, content, label: selected.label },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setTemplates(prev => ({ ...prev, [selected.key]: content }));
      setSuccess('Saved!');
    } catch (err) {
      setError('Failed to save');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleAddTemplate = async () => {
    if (!newTemplateKey || !newTemplateLabel) {
      setError('Both key and label are required');
      return;
    }

    // Check if key already exists
    if (templates[newTemplateKey] || OPTIONS.find(opt => opt.key === newTemplateKey) || customOptions.find(opt => opt.key === newTemplateKey)) {
      setError('A template with this key already exists');
      return;
    }

    try {
      const token = await getAccessToken();
      await axios.post(
        API_ENDPOINTS.TOS_TEMPLATES?.SAVE || '/api/tos-templates',
        { key: newTemplateKey, content: '', label: newTemplateLabel },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Add to custom options and select it
      const newOption = { key: newTemplateKey, label: newTemplateLabel };
      setCustomOptions(prev => [...prev, newOption]);
      setTemplates(prev => ({ ...prev, [newTemplateKey]: '' }));
      setSelected(newOption);
      setContent('');
      setShowAddModal(false);
      setNewTemplateKey('');
      setNewTemplateLabel('');
      setSuccess('Template created!');
    } catch (err) {
      setError('Failed to create template');
      console.error(err);
    }
  };

  const handleDeleteTemplate = async (templateKey) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      const token = await getAccessToken();
      await axios.delete(
        `${API_ENDPOINTS.TOS_TEMPLATES?.DELETE || '/api/tos-templates'}/${templateKey}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Remove from state
      setCustomOptions(prev => prev.filter(opt => opt.key !== templateKey));
      setTemplates(prev => {
        const newTemplates = { ...prev };
        delete newTemplates[templateKey];
        return newTemplates;
      });

      if (selected?.key === templateKey) {
        setSelected(null);
        setContent('');
      }

      setSuccess('Template deleted!');
    } catch (err) {
      setError('Failed to delete template');
      console.error(err);
    }
  };

  if (loading) {
    return <div className="tos-manager"><div className="tos-manager__loading">Loading...</div></div>;
  }

  return (
    <div className="tos-manager">
      <header className="tos-manager__header">
        <h1>TOS Template Manager</h1>
        <p>Edit the Terms of Service text for each split option.</p>
      </header>

      <div className="tos-manager__content">
        <aside className="tos-manager__sidebar">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3>Split Options</h3>
            <button 
              className="tos-manager__btn tos-manager__btn--success"
              onClick={() => setShowAddModal(true)}
              style={{ fontSize: '0.9rem', padding: '0.5rem 1rem' }}
            >
              + Add Template
            </button>
          </div>
          <div className="tos-manager__combinations">
            {OPTIONS.map((opt) => (
              <button
                key={opt.key}
                className={`tos-manager__combo-btn ${selected?.key === opt.key ? 'active' : ''} ${templates[opt.key] ? 'has-content' : ''}`}
                onClick={() => handleSelect(opt)}
              >
                <span className="combo-label">{opt.label}</span>
                <span className={`combo-status ${templates[opt.key] ? 'configured' : 'pending'}`}>
                  {templates[opt.key] ? '✓' : '○'}
                </span>
              </button>
            ))}
            
            {customOptions.length > 0 && (
              <>
                <div style={{ margin: '1rem 0', padding: '0.5rem 0', borderTop: '1px solid #ddd' }}>
                  <strong style={{ fontSize: '0.9rem', color: '#666' }}>Custom Templates</strong>
                </div>
                {customOptions.map((opt) => (
                  <button
                    key={opt.key}
                    className={`tos-manager__combo-btn ${selected?.key === opt.key ? 'active' : ''} ${templates[opt.key] ? 'has-content' : ''}`}
                    onClick={() => handleSelect(opt)}
                    style={{ position: 'relative' }}
                  >
                    <span className="combo-label">{opt.label}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span className={`combo-status ${templates[opt.key] ? 'configured' : 'pending'}`}>
                        {templates[opt.key] ? '✓' : '○'}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteTemplate(opt.key);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#dc3545',
                          cursor: 'pointer',
                          padding: '0.25rem',
                          fontSize: '1rem'
                        }}
                        title="Delete template"
                      >
                        🗑️
                      </button>
                    </div>
                  </button>
                ))}
              </>
            )}
          </div>
        </aside>

        <main className="tos-manager__editor">
          {selected ? (
            <>
              <div className="tos-manager__editor-header">
                <h3>{selected.label}</h3>
                <button 
                  className="tos-manager__btn tos-manager__btn--primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
              
              {error && <div className="tos-manager__error">{error}</div>}
              {success && <div className="tos-manager__success">{success}</div>}
              
              <textarea
                className="tos-manager__textarea"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Paste or type the TOS text here..."
              />
              
              <div className="tos-manager__preview">
                <h4>Preview</h4>
                <div className="tos-manager__preview-content">
                  <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{content}</pre>
                </div>
              </div>
            </>
          ) : (
            <div className="tos-manager__placeholder">
              <div className="placeholder-icon">📝</div>
              <h3>Select an Option</h3>
              <p>Click an option on the left to edit its TOS text.</p>
            </div>
          )}
        </main>
      </div>

      {/* Add Template Modal */}
      {showAddModal && (
        <div className="tos-manager__modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="tos-manager__modal" onClick={(e) => e.stopPropagation()}>
            <div className="tos-manager__modal-header">
              <h3>Add New Template</h3>
              <button 
                className="tos-manager__modal-close"
                onClick={() => setShowAddModal(false)}
              >
                ×
              </button>
            </div>
            <div className="tos-manager__modal-body">
              <p style={{ color: '#a0aec0', fontSize: '0.9rem', marginBottom: '1.5rem', lineHeight: '1.5' }}>
                Create a custom Terms of Service template for special arrangements or unique agreements.
              </p>
              
              <div className="tos-manager__form-group">
                <label htmlFor="template-key">Template Key *</label>
                <input
                  id="template-key"
                  type="text"
                  className="tos-manager__input"
                  value={newTemplateKey}
                  onChange={(e) => setNewTemplateKey(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                  placeholder="e.g., custom-option-1"
                />
                <small style={{ color: '#a0aec0', fontSize: '0.85rem', display: 'block', marginTop: '0.25rem' }}>
                  <strong>Internal identifier</strong> stored in the database. Use lowercase letters, numbers, and hyphens only.
                </small>
              </div>
              
              <div className="tos-manager__form-group">
                <label htmlFor="template-label">Display Label *</label>
                <input
                  id="template-label"
                  type="text"
                  className="tos-manager__input"
                  value={newTemplateLabel}
                  onChange={(e) => setNewTemplateLabel(e.target.value)}
                  placeholder="e.g., Custom Option 1"
                />
                <small style={{ color: '#a0aec0', fontSize: '0.85rem', display: 'block', marginTop: '0.25rem' }}>
                  <strong>Human-readable name</strong> shown in dropdown menus and approval links.
                </small>
              </div>

              {error && <div className="tos-manager__error" style={{ marginTop: '1rem' }}>{error}</div>}
            </div>
            <div className="tos-manager__modal-footer">
              <button 
                className="tos-manager__btn"
                onClick={() => {
                  setShowAddModal(false);
                  setNewTemplateKey('');
                  setNewTemplateLabel('');
                  setError(null);
                }}
              >
                Cancel
              </button>
              <button 
                className="tos-manager__btn tos-manager__btn--primary"
                onClick={handleAddTemplate}
              >
                Create Template
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TOSTemplateManager;
